import type { ProductAttribute } from '@akademi/site-builder-runtime';
import type { LineTotal } from '../home/money';
import type { SolarListingDocument, VatBasis } from './types';

/**
 * What a listing card is allowed to say about a product, and how each figure is reached.
 *
 * <p>Pure functions, deliberately: every one of them is a claim printed next to a price on a
 * trading shop, and the only way to hold them honest is to be able to test them without a
 * renderer. {@link SolarListingGrid} draws what these return and decides nothing itself.
 */

/** The spec rows a card may draw, in the order the backend sent them, capped. */
export const MAX_SPEC_ROWS = 3;

/** A total arranged for printing: the figure on the basis asked for, and the other one. */
export interface ShownTotal {
  currency: string;
  /** The figure the card prints, in minor units. */
  shown: number;
  /** Whether {@link shown} includes tax — the label that must sit beside it. */
  shownInclusive: boolean;
  /** The same total on the other basis, where the backend sent one. */
  other?: number;
  /** Whether {@link other} includes tax. Present exactly when `other` is. */
  otherInclusive?: boolean;
}

/**
 * A total on the basis the page is reading, with the other basis beside it.
 *
 * <p><strong>No tax arithmetic.</strong> This only chooses between two figures the backend
 * already sent — the published one and its `alternate` — exactly as the storefront's own
 * `onBasis` does for `<Price>`. When the backend could not resolve the product's tax there
 * is no alternate, so the published figure is returned with its OWN label however the switch
 * is set: an ex VAT figure honestly labelled beats an inc VAT figure guessed in the browser,
 * and it is also what keeps the pence per watt beside it true.
 */
export function totalOnBasis(total: LineTotal, basis: VatBasis): ShownTotal {
  const wanted = basis === 'inc';
  const swap = total.publishedInclusive !== wanted && total.alternate !== undefined;
  if (!swap) {
    return {
      currency: total.currency,
      shown: total.published,
      shownInclusive: total.publishedInclusive,
      ...(total.alternate === undefined
        ? {}
        : { other: total.alternate, otherInclusive: !total.publishedInclusive }),
    };
  }
  return {
    currency: total.currency,
    shown: total.alternate!,
    shownInclusive: !total.publishedInclusive,
    other: total.published,
    otherInclusive: total.publishedInclusive,
  };
}

/** A bare number with no unit and no words — "30", "23.1". */
const BARE_NUMBER = /^-?\d+(?:[.,]\d+)?$/;

/**
 * The attributes that read as a specification, in order.
 *
 * <p><strong>An unlabelled attribute is never drawn.</strong> An answer reaches the card
 * either as an `AcceptableValue` label ("460 W", "54 cells"), which reads on its own, or as a
 * bare `decimalValue` whose unit was frequently never recorded — and a row reading
 * "Panel Frame  30" tells a buyer nothing and looks like a fault. The backend already drops
 * those (`CatalogueQueryService.cardAttributes` takes labelled answers first), so this is the
 * second lock on the same door rather than the only one: a card is the wrong place to find
 * out that an API changed its mind.
 *
 * <p>An attribute with no field name is dropped for the same reason — the value would stand
 * in a table with nothing saying what it measures.
 */
export function specRows(
  attributes: readonly ProductAttribute[] | undefined,
  limit: number = MAX_SPEC_ROWS,
): ProductAttribute[] {
  return (attributes ?? [])
    .filter((attribute) => {
      const label = attribute?.label?.trim();
      const value = attribute?.value?.trim();
      return !!label && !!value && !BARE_NUMBER.test(value);
    })
    .slice(0, limit);
}

/** A product's peak wattage, and the attribute it was read from. */
export interface Wattage {
  /** Watts, as a positive number. */
  watts: number;
  /** The `CustomField` id the figure came from, so the card can say where it got it. */
  code: string;
  /** The attribute's own display label ("Power"), for the figure's accessible description. */
  label: string;
}

/**
 * Watts, where the card's own attributes state them.
 *
 * <p><strong>Read from the attribute's UNIT, not from a code.</strong> `ProductAttributeDto`
 * documents `code` as "the `CustomField` id as a string" — a per-tenant surrogate key, minted
 * when `create-attributes.py` creates the field. It is stable for one shop and means nothing
 * across two, so a literal pinned here would at best go quiet on a rebuilt schema and at
 * worst land on a different field: `pv.power_w` is id N on this shop and "Cells" may be id N
 * on the next, and the card would then print a price per cell labelled per watt. There is no
 * business key on `CustomField` anywhere in this codebase to pin instead.
 *
 * <p>What is unambiguous is the unit the answer carries: a value of "460 W" IS four hundred
 * and sixty watts in any shop, in any language, under any field id. So the figure is parsed
 * out of the displayed value, and the attribute's id is returned beside it so a card can
 * record which row it divided by.
 *
 * <p><strong>Only `W` and `Wp`.</strong> An inverter's "Rated output 10 kW" is a different
 * quantity — continuous AC output, not peak DC — and pounds per AC watt is not the figure a
 * panel listing is being compared on. Accepting kW here would quietly put a price-per-watt on
 * every inverter card, so it does not.
 */
export function wattageOf(attributes: readonly ProductAttribute[] | undefined): Wattage | null {
  for (const attribute of attributes ?? []) {
    const watts = wattsIn(attribute?.value);
    if (watts !== null) {
      return { watts, code: attribute.code ?? '', label: attribute.label ?? '' };
    }
  }
  return null;
}

const WATT_VALUE = /^(\d+(?:[.,]\d+)?)\s*(W|Wp)$/;

/** The watts a display value states, or null when it does not state any. */
function wattsIn(value: string | undefined): number | null {
  const match = WATT_VALUE.exec((value ?? '').trim());
  if (!match) return null;
  const watts = Number(match[1]!.replace(',', '.'));
  return Number.isFinite(watts) && watts > 0 ? watts : null;
}

/**
 * Currencies whose minor unit has a symbol a shopper reads without a decimal point.
 *
 * <p>"20.7p per watt" is how a panel is quoted in this trade; "£0.207" is not. Nothing else
 * is assumed: a currency that is not here is formatted as money per watt instead, which is
 * correct everywhere and merely less idiomatic.
 */
const MINOR_UNIT_SUFFIX: Record<string, string> = { GBP: 'p' };

/**
 * A price per watt, from a price already in minor units on a chosen tax basis.
 *
 * <p><strong>Display arithmetic only, and no tax in it.</strong> The caller passes the figure
 * it is about to print — the published basis or the backend's `alternate`, never a basis
 * derived here — so the pence-per-watt beside an inc VAT price is computed from the inc VAT
 * price, and the one beside an ex VAT price from the ex VAT price. Nothing in this file adds
 * or removes VAT, and no backend field carries this number.
 */
export function formatPerWatt(
  minor: number,
  watts: number,
  currency: string,
  locale: string,
): string | null {
  if (!Number.isFinite(minor) || minor <= 0 || !Number.isFinite(watts) || watts <= 0) return null;
  const perWatt = minor / watts;
  const suffix = MINOR_UNIT_SUFFIX[currency];
  if (suffix && perWatt < 100) {
    return `${decimals(perWatt, locale, 1)}${suffix}`;
  }
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(perWatt / 100);
  } catch {
    return `${currency} ${(perWatt / 100).toFixed(4)}`;
  }
}

function decimals(value: number, locale: string, places: number): string {
  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: places,
      maximumFractionDigits: places,
    }).format(value);
  } catch {
    return value.toFixed(places);
  }
}

/**
 * The product's datasheet, where it has one.
 *
 * <p>Only a DATASHEET. The approved mockup draws a second "Spec sheet" link beside it and
 * this catalogue has no such document type — every document on it is a DATASHEET, a MANUAL or
 * a WARRANTY — so a second link would either 404 or point at the same PDF under a second
 * name. The specification the mockup's "spec sheet" promises is the table on the card itself.
 *
 * <p>Matched case-insensitively on `documentType`, falling back to the runtime's `kind`, which
 * the storefront fills from the same column. A document with no type at all is not assumed to
 * be a datasheet: an unlabelled PDF offered as one is a claim about a file nobody checked.
 */
export function datasheetOf(
  documents: readonly SolarListingDocument[] | undefined,
): SolarListingDocument | null {
  for (const document of documents ?? []) {
    const type = (document?.documentType ?? document?.kind ?? '').trim().toUpperCase();
    if (type === 'DATASHEET' && document.url) return document;
  }
  return null;
}
